import { ArrowUpCircle } from "lucide-react";
import { MACHINE_UPDATE_COMMAND, MIN_MACHINE_VERSION_DEFAULT_KEY } from "@mycompbox/shared";
import { machineMinimumVersion, type Machine } from "@/lib/api/mailbox";

/**
 * Warning shown when a machine's CompBox CLI is below the minimum version the
 * API requires for a job kind (ENG-322). The server refuses to dispatch to such
 * a machine, so this tells the user exactly how to fix it.
 */
export function OutdatedMachineNotice({
  machine,
  jobKind = MIN_MACHINE_VERSION_DEFAULT_KEY,
  className = "rounded-md border border-amber-800/30 bg-amber-900/[0.06] p-3 text-sm",
}: {
  machine: Pick<Machine, "name"> & Partial<Pick<Machine, "cliVersion" | "minMachineVersion">>;
  jobKind?: string;
  className?: string;
}) {
  const minimum = machineMinimumVersion(machine, jobKind);
  return (
    <div className={className} data-testid="outdated-machine-notice">
      <div className="flex items-center gap-2 font-medium">
        <ArrowUpCircle className="h-4 w-4 text-amber-400" />
        <span className="text-amber-300">CompBox update required</span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        <strong>{machine.name}</strong> is running CompBox{" "}
        {machine.cliVersion ? `v${machine.cliVersion}` : "an older version"}
        {minimum ? `, but v${minimum} or newer is required` : ", which is no longer supported"}
        . Run{" "}
        <code className="bg-muted px-1 py-0.5 rounded text-xs">{MACHINE_UPDATE_COMMAND}</code>{" "}
        on that machine, then try again. This will update automatically once it checks in.
      </p>
    </div>
  );
}
